// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useMemo } from "react";
import AvailabilityViewHeader from "../../components/availabilityView/AvailabilityViewHeader";
import AvailabilityFilterBar from "../../components/availabilityView/AvailabilityFilterBar";
import AvailabilityTypeFilter from "../../components/availabilityView/AvailabilityTypeFilter";
import AvailabilityStats from "../../components/availabilityView/AvailabilityStats";
import AvailabilityCalendar from "../../components/availabilityView/AvailabilityCalendar";
import AvailabilityList from "../../components/availabilityView/AvailabilityList";
import AvailabilityDetailPanel from "../../components/availabilityView/AvailabilityDetailPanel";
import { useAvailabilityView } from "../../components/availabilityView/hooks/useAvailabilityView";
import InlineLoader from "../../components/InlineLoader";
import { AlertTriangle } from "lucide-react";
import "../../components/availabilityView/AvailabilityViewPreview.css";

const AvailabilityViewPreview = () => {
  const {
    currentView,
    setCurrentView,
    currentMonth,
    changeMonth,
    filters,
    updateFilter,
    locationOptions,
    scenarioOptions,
    tenantOptions,
    isSuperAdmin,
    activeTypes,
    toggleType,
    filteredData,
    stats,
    detailDate,
    detailItems,
    panelClosing,
    openDetail,
    closeDetail,
    viewStatus,
    isLoading,
    error,
    calendarRef,
    listRef,
  } = useAvailabilityView();

  const filterSummary = useMemo(() => {
    const loc =
      locationOptions.find((o) => o.value === filters.location)?.label ||
      "Standort wählen";
    const scen =
      scenarioOptions.find((o) => o.value === filters.scenario)?.label ||
      "Alle Szenarien";
    return `${loc} · ${scen}`;
  }, [filters, locationOptions, scenarioOptions]);

  return (
    <div className="availability-view">
      <a href="#av-main" className="av-skip-link">
        Zum Hauptinhalt springen
      </a>
      <div className="av-view-status" aria-live="polite" aria-atomic="true">
        {viewStatus}
      </div>

      <main id="av-main" className="av-app">
        <AvailabilityViewHeader
          filterSummary={filterSummary}
          currentView={currentView}
          onViewChange={setCurrentView}
          tenant={filters.tenant}
          onTenantChange={(value) => updateFilter("tenant", value)}
          tenantOptions={tenantOptions}
          showTenantSelect={isSuperAdmin}
        />

        <AvailabilityFilterBar
          filters={filters}
          onFilterChange={updateFilter}
          locationOptions={locationOptions}
          scenarioOptions={scenarioOptions}
        />

        <AvailabilityTypeFilter
          activeTypes={activeTypes}
          onToggleType={toggleType}
        />

        <AvailabilityStats stats={stats} />

        {error && (
          <div className="av-error-banner" role="alert">
            <AlertTriangle size={16} aria-hidden />
            <span>
              Belegte Tage konnten nicht geladen werden – es werden keine
              aktuellen Daten angezeigt.
            </span>
          </div>
        )}

        {isLoading && (
          <InlineLoader size="sm" message="Belegte Tage werden geladen …" />
        )}

        {currentView === "calendar" ? (
          <section ref={calendarRef} aria-label="Kalenderansicht">
            <AvailabilityCalendar
              currentMonth={currentMonth}
              filteredData={filteredData}
              onMonthChange={changeMonth}
              onDayClick={openDetail}
            />
          </section>
        ) : (
          <section ref={listRef} aria-label="Listenansicht">
            <AvailabilityList
              filteredData={filteredData}
              onRowClick={openDetail}
            />
          </section>
        )}
      </main>

      <AvailabilityDetailPanel
        isOpen={Boolean(detailDate)}
        isClosing={panelClosing}
        detailDate={detailDate}
        detailItems={detailItems}
        onClose={closeDetail}
      />
    </div>
  );
};

export default AvailabilityViewPreview;
